/**
 * GlobalDeclareJsonColumnDialog
 *
 * App-level mount of DeclareJsonColumnDialog driven by
 * `useDeclareJsonColumnDialogStore`. Activated from grid context menus
 * ("Declare as JSON" on column header / cell) so the same confirmation UX
 * runs outside the filter-chip path.
 */

import { useCallback, useMemo, useState } from 'react';
import { DeclareJsonColumnDialog } from './DeclareJsonColumnDialog';
import { useDeclareJsonColumnDialogStore } from './declareJsonColumnDialogStore';
import { useMetaInfo, useInvalidateMetaInfo } from '../../hooks/useMetaInfo';
import { addJsonColumn } from '../../services/metaInfoApi';
import {
  categorizeJsonColumn,
  isNativeJsonColumn,
  type JsonColumnInput,
} from '../../utils/jsonColumnCategorization';
import { getEngineLabel } from '../../utils/engineLabel';
import {
  isDeclareOnTextSupported,
  MIN_PG_MAJOR_VERSION_FOR_DECLARED_JSON,
} from '../../utils/filterUtils';
import { usePgServerMajorVersion } from '../../hooks/usePgServerMajorVersion';
import { useAnalytics } from '../../../../../core/analytics';

const friendlyDeclareError = (err: unknown): string => {
  if (err instanceof Error && err.message) return err.message;
  return 'Failed to declare the column as JSON. Check your connection and try again.';
};

const newDeclarationId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `json-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export function GlobalDeclareJsonColumnDialog() {
  const { isOpen, context, closeDialog } = useDeclareJsonColumnDialogStore();
  const invalidate = useInvalidateMetaInfo();
  const { track } = useAnalytics();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const metaInfoQuery = useMetaInfo(
    context?.connectionId ?? null,
    context?.database ?? null,
  );

  const pgMajorVersion = usePgServerMajorVersion(
    context?.connectionId,
    context?.database,
    context?.engine,
  );

  /**
   * The column as the categorizer sees it. Built once, so the confirm path
   * and the PG version gate below cannot disagree about what this column is.
   */
  const jsonInput = useMemo<JsonColumnInput | null>(
    () =>
      context
        ? {
            engine: context.engine,
            schema: context.schema,
            table: context.table,
            name: context.column,
            dataType: context.dataType ?? '',
          }
        : null,
    [context],
  );

  const handleClose = useCallback(() => {
    setErrorMessage(null);
    closeDialog();
  }, [closeDialog]);

  const handleConfirm = useCallback(async () => {
    if (!context || !jsonInput) return;
    setErrorMessage(null);
    try {
      // If already declared (e.g. raced with another surface), short-circuit
      // to a friendly close rather than a duplicate write.
      const cat = categorizeJsonColumn(jsonInput, metaInfoQuery.data);
      if (cat === 'declared-json') {
        // Race: another surface already declared it. No-op close.
        // (Native columns without a declaration row are allowed through —
        // a declaration row stores user-supplied path metadata / loose-FK
        // targets regardless of native vs. text column type.)
        handleClose();
        return;
      }
      await addJsonColumn(context.connectionId, context.database, {
        id: newDeclarationId(),
        schema: context.schema,
        table: context.table,
        column: context.column,
        createdAt: new Date().toISOString(),
      });
      track('feature:json_declare', {
        engine: context.engine,
        source: context.source,
      });
      await invalidate(context.connectionId, context.database);
      handleClose();
    } catch (err) {
      setErrorMessage(friendlyDeclareError(err));
    }
  }, [context, jsonInput, handleClose, invalidate, metaInfoQuery.data, track]);

  if (!isOpen || !context || !jsonInput) return null;

  // Build the same PG version-gate banner the filter-chip path uses.
  //
  // Native `json`/`jsonb` columns are exempt: the PG 16 floor exists only
  // because the declared-on-TEXT path emits the `IS JSON` predicate, which a
  // native column never takes — declaring one just stores path metadata and
  // loose-FK targets, and works on any server. Checking it HERE rather than
  // relying on the caller's `mode` prop (which `DeclareJsonColumnDialog` uses
  // to suppress the banner) keeps the gate correct on its own: today every
  // caller that could pass a native column sets `mode: 'native'`, and the ones
  // that don't are gated upstream to json-eligible columns, but that is two
  // invariants holding at a distance rather than one enforced locally. This is
  // the same exemption `metainfo_declare`'s json branch applies.
  const unsupportedReason =
    context.engine === 'pgsql' &&
    !isNativeJsonColumn(jsonInput) &&
    !isDeclareOnTextSupported({
      engine: context.engine,
      schema: context.schema,
      table: context.table,
      metaInfo: metaInfoQuery.data ?? null,
      pgMajorVersion,
    })
      ? `Declaring text columns as JSON requires PostgreSQL ${MIN_PG_MAJOR_VERSION_FOR_DECLARED_JSON}+ (uses the IS JSON predicate).${
          typeof pgMajorVersion === 'number'
            ? ` Detected PostgreSQL ${pgMajorVersion}.`
            : ''
        } Use a native jsonb column for JSON filtering on this server.`.trim()
      : null;

  return (
    <DeclareJsonColumnDialog
      open
      schema={context.schema}
      table={context.table}
      column={context.column}
      dataType={context.dataType}
      engineLabel={getEngineLabel(context.engine)}
      errorMessage={errorMessage}
      unsupportedReason={unsupportedReason}
      sampleRows={context.sampleValues}
      connectionId={context.connectionId}
      database={context.database}
      engine={context.engine}
      mode={context.mode ?? 'eligible'}
      onConfirm={handleConfirm}
      onCancel={handleClose}
    />
  );
}
